import React, { useEffect, useMemo, useState } from 'react';
import * as api from '../../api';

const BTN = "inline-flex items-center gap-1 rounded-lg border border-[#1f2638] bg-[#121825] px-2.5 py-1.5 text-xs hover:bg-[#141b2d] focus:outline-none focus:ring-2 focus:ring-violet-600/40 disabled:opacity-60 disabled:cursor-not-allowed";
const BTN_PRIMARY = "inline-flex items-center gap-1 rounded-lg border border-violet-600/40 bg-violet-600/20 px-2.5 py-1.5 text-xs hover:bg-violet-600/30 focus:ring-2 focus:ring-violet-600/60 disabled:opacity-60 disabled:cursor-not-allowed";
const CHIP = "inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border";

const fmt = (n) => new Intl.NumberFormat('bg-BG').format(Number(n || 0));

const EngineBadge = ({ engine }) => {
    const e = (engine || '').toLowerCase();
    const style = e === 'gemini'
        ? 'bg-sky-600/15 border-sky-500/30 text-sky-300'
        : 'bg-emerald-600/15 border-emerald-500/30 text-emerald-300';
    return <span className={`${CHIP} ${style}`}>{e || '—'}</span>;
};

const TokenCard = ({ m }) => {
    const isGemini = (m.engine || '').toLowerCase() === 'gemini';

    const items = [
        { label: 'Calls', value: fmt(m.calls_total) },
        { label: 'Prompt in', value: fmt(m.prompt_total) },
        ...(isGemini ? [{ label: 'Audio in', value: fmt(m.audio_total) }] : []),
        { label: 'Output', value: fmt(m.out_total) },
        { label: 'Total', value: fmt(m.total) },
    ];

    return (
        <div className="rounded-2xl border border-[#1f2638] bg-[#0e1320]/50 p-4">
            <div className="flex items-center gap-2">
                <div className="font-semibold">{m.model}</div>
                <EngineBadge engine={m.engine} />
                {m.is_default && (
                    <span className={`${CHIP} bg-emerald-600/15 border-emerald-500/30 text-emerald-300`}>default</span>
                )}
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                {items.map(it => (
                    <div key={it.label} className="rounded-xl border border-[#1f2638] bg-[#121825] p-3">
                        <div className="text-slate-400 text-xs">{it.label}</div>
                        <div className="mt-1 text-lg font-bold">{it.value}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const Slider = ({ value, min = 0, max = 1, step = 0.1, onChange }) => {
    const pct = Math.round(((Number(value) - min) / (max - min)) * 100);
    return (
        <div className="flex items-center gap-3">
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={Number(value ?? 0)}
                onChange={onChange}
                className="range-vb flex-1"
                style={{ '--pct': `${pct}%` }}
            />
            <div className="w-14 text-left text-sm tabular-nums text-slate-300">
                {Number(value ?? 0).toFixed(1)}
            </div>
        </div>
    );
};

const Switch = ({ checked, onChange, label }) => (
    <label className="inline-flex items-center gap-3 cursor-pointer select-none">
        <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            className="sr-only peer"
        />
        <span
            className={[
                "relative w-11 h-6 rounded-full border transition",
                "border-[#1f2638] bg-[#121825]",
                "peer-checked:bg-violet-600/40 peer-checked:border-violet-600/50",
                "after:content-[''] after:absolute after:top-1 after:left-1",
                "after:h-4 after:w-4 after:rounded-full after:bg-white/80 after:shadow",
                "after:transition-transform peer-checked:after:translate-x-5"
            ].join(' ')}
        />
        {label && <span className="text-slate-300 text-sm">{label}</span>}
    </label>
);

export default function Models({ onBack }) {
    const [loading, setLoading] = useState(false);
    const [models, setModels] = useState([]);
    const [activeModel, setActiveModel] = useState(null);
    const [toast, setToast] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 2200);
        return () => clearTimeout(t);
    }, [toast]);

    const reload = async () => {
        setLoading(true);
        try {
            const list = await api.listModels();
            setModels(list);
            setActiveModel(list?.[0]?.model || null);
        } catch (e) {
            console.error(e);
            setToast('Грешка при зареждане на модели.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, []);

    const active = useMemo(
        () => models.find(m => m.model === activeModel) || null,
        [models, activeModel]
    );

    const patchActiveLocal = (p) => {
        setModels(prev => prev.map(m => m.model === activeModel ? { ...m, ...p } : m));
    };

    const validateFields = (m) => {
        if (m.max_tokens < 0 || m.max_tokens > 100000) throw new Error('max_tokens трябва да е между 0 и 100000.');
        if (m.temperature < 0 || m.temperature > 1) throw new Error('temperature трябва да е между 0 и 1.');
        if (m.engine === 'gemini' && ![0, 1].includes(Number(m.thinking_mode ?? 0))) throw new Error('thinking_mode трябва да е 0 или 1.');
    };

    const save = async () => {
        if (!active) return;
        try {
            const payload = {
                is_default: Boolean(active.is_default),
                max_tokens: Number(active.max_tokens ?? 0),
                temperature: Number(active.temperature ?? 0),
            };
            if (active.engine === 'gemini') {
                payload.thinking_mode = Number(active.thinking_mode ?? 0);
            }
            validateFields({ ...active, ...payload });
            setSaving(true);
            const updated = await api.updateModel(active.model, payload);
            setModels(prev => prev.map(m => m.model === active.model ? updated : m));
            if (payload.is_default) {
                setModels(prev => prev.map(m => ({ ...m, is_default: m.model === active.model })));
            }
            setToast('Записано.');
        } catch (e) {
            console.error(e);
            setToast(e?.response?.data?.detail || e?.message || 'Грешка при запис.');
        } finally {
            setSaving(false);
        }
    };

    const makeDefault = async () => {
        if (!active || active.is_default) return;
        try {
            setSaving(true);
            const updated = await api.updateModel(active.model, { is_default: true });
            await reload();
            setActiveModel(updated.model);
            setToast('Маркиран като дефолтен.');
        } catch (e) {
            console.error(e);
            setToast(e?.response?.data?.detail || 'Грешка при промяна на дефолтен.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6 space-y-6">
            {toast && (
                <div className="fixed right-4 top-4 z-50">
                    <div className="rounded-xl border border-emerald-500/30 bg-emerald-600/15 px-3 py-2 text-sm text-emerald-200 shadow-lg">
                        {toast}
                    </div>
                </div>
            )}

            <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold">Модели</h1>
                {typeof onBack === 'function' && (
                    <button className={BTN} onClick={onBack}>← Назад</button>
                )}
                <div className="ml-auto text-xs text-slate-500">{loading ? 'зареждане…' : ''}</div>
            </div>

            <div className="grid grid-cols-1 gap-4">

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {models.map(m => (
                        <TokenCard key={m.id || m.model} m={m} />
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[300px,1fr] gap-6">
                <div className="rounded-2xl border border-[#1f2638] bg-[#0e1320]/50">
                    <div className="p-3 text-sm text-slate-400">Налични модели</div>
                    <div className="divide-y divide-[#1f2638]">
                        {models.map(m => {
                            const isActive = activeModel === m.model;
                            return (
                                <div
                                    key={m.id || m.model}
                                    className={`p-3 cursor-pointer hover:bg-[#0e1320] ${isActive ? 'bg-[#0e1320]' : ''}`}
                                    onClick={() => setActiveModel(m.model)}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="font-semibold text-slate-200 truncate">{m.model}</div>
                                        <EngineBadge engine={m.engine} />
                                        {m.is_default && (
                                            <span className={`${CHIP} bg-emerald-600/15 border-emerald-500/30 text-emerald-300`}>default</span>
                                        )}
                                    </div>
                                    <div className="mt-1 text-xs text-slate-500">
                                        temp {m.temperature ?? 0} · max_tokens {m.max_tokens ?? 0}
                                        {m.engine === 'gemini' && ` · thinking ${Number(m.thinking_mode ?? 0) ? 'on' : 'off'}`}
                                    </div>
                                </div>
                            );
                        })}
                        {models.length === 0 && (
                            <div className="p-3 text-sm text-slate-500">Няма модели. seed-ът ще ги добави при бекенд старт.</div>
                        )}
                    </div>
                </div>

                <div className="rounded-2xl border border-[#1f2638] bg-[#0e1320]/50 p-4">
                    {!active ? (
                        <div className="text-slate-400">Избери модел отляво.</div>
                    ) : (
                        <>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-slate-400">Model</label>
                                    <div className="mt-1 bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm text-slate-300">
                                        {active.model}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm text-slate-400">Engine</label>
                                    <div className="mt-1 bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm text-slate-300 flex items-center gap-2">
                                        {active.engine}
                                        <EngineBadge engine={active.engine} />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm text-slate-400">Max tokens (0-100000)</label>
                                    <input
                                        type="number" min={0} max={100000}
                                        value={Number(active.max_tokens ?? 0)}
                                        onChange={e => {
                                            let v = Number(e.target.value);
                                            if (isNaN(v)) v = 0;
                                            v = Math.max(0, Math.min(100000, v));
                                            patchActiveLocal({ max_tokens: v });
                                        }}
                                        className="mt-1 w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                                    />
                                </div>

                                <div className="sm:col-span-2">
                                    <label className="text-sm text-slate-400">Temperature (0–1)</label>
                                    <div className="mt-2 w-[620px]">
                                        <Slider
                                            value={active.temperature}
                                            min={0}
                                            max={1}
                                            step={0.1}
                                            onChange={e => patchActiveLocal({ temperature: Number(e.target.value) })}
                                        />
                                    </div>
                                </div>

                                {active.engine === 'gemini' && (
                                    <div className="sm:col-span-2">
                                        <label className="text-sm text-slate-400">Thinking mode</label>
                                        <div className="mt-2 flex items-center gap-3">
                                            <Switch
                                                checked={Number(active.thinking_mode ?? 0) === 1}
                                                onChange={e => patchActiveLocal({ thinking_mode: e.target.checked ? 1 : 0 })}
                                                label="Активирай"
                                            />
                                            <span className="text-xs text-slate-500">Достъпно само за Gemini модели.</span>
                                        </div>
                                    </div>
                                )}

                                <div className="sm:col-span-2">
                                    <label className="text-sm text-slate-400">Дефолтен</label>
                                    <div className="mt-2 flex items-center gap-2">
                                        {active.is_default ? (
                                            <>
                                                <span className={`${CHIP} bg-emerald-600/15 border-emerald-500/30 text-emerald-300`}>default</span>
                                                <span className="text-xs text-slate-500">Текущ глобален дефолтен модел.</span>
                                            </>
                                        ) : (
                                            <button className={BTN} onClick={makeDefault} disabled={saving}>
                                                Направи дефолтен
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 flex items-center justify-end gap-2">
                                <button className={BTN_PRIMARY} onClick={save} disabled={saving || !active}>
                                    Запази
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}